import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Badge as BadgeIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { formatCurrency } from '../utils/currency';
import { User, Role } from '../types';
import { 
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation
} from '../services/users.service';
import { useGetLocationsQuery } from '../services/locations.service';

interface UserFormData {
  email: string;
  name: string;
  password: string;
  role: Role;
  locationId?: string;
  employeeCode?: string;
  commissionRate: number;
  baseSalary?: number;
  hireDate?: string;
}

const UsersPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Queries
  const { data: usersResponse, isLoading, error, refetch } = useGetUsersQuery({
    search: searchTerm.length >= 2 ? searchTerm : undefined,
    role: selectedRole || undefined,
    locationId: selectedLocation || undefined,
    isActive: true,
    limit: 100,
  });

  const { data: locationsResponse } = useGetLocationsQuery({ active: true });

  // Mutations
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  const { 
    control, 
    handleSubmit, 
    reset, 
    formState: { errors } 
  } = useForm<UserFormData>({
    defaultValues: {
      email: '',
      name: '',
      password: '',
      role: Role.CASHIER,
      locationId: '',
      employeeCode: '',
      commissionRate: 1.00,
      baseSalary: 0,
      hireDate: '',
    },
  });

  const users = usersResponse?.users || [];
  const locations = locationsResponse?.data || [];

  const getRoleColor = (role: Role) => {
    switch (role) {
      case Role.ADMIN: return 'error';
      case Role.MANAGER: return 'warning';
      case Role.CASHIER: return 'primary';
      default: return 'default';
    }
  };

  const getRoleLabel = (role: Role) => {
    switch (role) {
      case Role.ADMIN: return 'Administrador';
      case Role.MANAGER: return 'Gerente';
      case Role.CASHIER: return 'Cajero';
      default: return role;
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      reset({
        email: user.email,
        name: user.name,
        password: '', // No mostrar contraseña existente
        role: user.role,
        locationId: user.locationId || '',
        employeeCode: user.employeeCode || '',
        commissionRate: user.commissionRate || 1.00,
        baseSalary: user.baseSalary || 0,
        hireDate: user.hireDate ? user.hireDate.split('T')[0] : '',
      });
    } else {
      setEditingUser(null);
      reset();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    reset();
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      if (editingUser) {
        const updateData: any = { ...data };
        if (!updateData.password) {
          delete updateData.password; // No actualizar contraseña si está vacía
        }
        // Convert empty strings to null for optional fields
        if (updateData.employeeCode === '') updateData.employeeCode = null;
        if (updateData.locationId === '') updateData.locationId = null;
        if (updateData.baseSalary === 0 || updateData.baseSalary === '') updateData.baseSalary = null;
        if (updateData.hireDate === '') updateData.hireDate = null;
        
        console.log('🔍 Frontend - Sending update data:', updateData);
        await updateUser({ id: editingUser.id, data: updateData }).unwrap();
      } else {
        await createUser(data).unwrap();
      }
      handleCloseDialog();
      refetch();
    } catch (error: any) {
      console.error('Error al guardar usuario:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
    }
  };

  const handleDeleteUser = async () => {
    if (userToDelete) {
      try {
        await deleteUser(userToDelete.id).unwrap();
        setOpenDeleteDialog(false);
        setUserToDelete(null);
        refetch();
      } catch (error: any) {
        console.error('Error al eliminar usuario:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los usuarios. {error && 'data' in error ? String(error.data) : 'Error desconocido'}
        </Alert>
        <Button variant="contained" onClick={() => refetch()}>
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Gestión de Usuarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="large"
        >
          Nuevo Usuario
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, email o código de empleado..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Rol</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                label="Rol"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value={Role.ADMIN}>Administrador</MenuItem>
                <MenuItem value={Role.MANAGER}>Gerente</MenuItem>
                <MenuItem value={Role.CASHIER}>Cajero</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Ubicación</InputLabel>
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                label="Ubicación"
              >
                <MenuItem value="">Todas</MenuItem>
                {locations.map((location: any) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setSelectedRole('');
                setSelectedLocation('');
              }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Lista de usuarios */}
      <Grid container spacing={3}>
        {users.map((user) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={user.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="div" fontWeight="bold">
                    {user.name}
                  </Typography>
                  <Chip 
                    label={getRoleLabel(user.role)} 
                    color={getRoleColor(user.role)} 
                    size="small" 
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  📧 {user.email}
                </Typography>
                
                {user.employeeCode && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    👤 Código: {user.employeeCode}
                  </Typography>
                )}
                
                {user.location && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    📍 {user.location.name}
                  </Typography>
                )}
                
                {user.baseSalary && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    💰 Salario: {formatCurrency(user.baseSalary)}
                  </Typography>
                )}
                
                {user.commissionRate && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    📈 Comisión: {user.commissionRate}%
                  </Typography>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <Chip 
                    label={user.isActive ? 'Activo' : 'Inactivo'} 
                    color={user.isActive ? 'success' : 'default'}
                    size="small" 
                  />
                </Box>
              </CardContent>
              
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Box>
                  {user.employeeCode && (
                    <Tooltip title="Código de empleado">
                      <IconButton size="small" color="primary">
                        <QrCodeIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {user.baseSalary && (
                    <Tooltip title="Empleado con salario">
                      <IconButton size="small" color="success">
                        <MoneyIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(user)}
                    sx={{ mr: 1 }}
                  >
                    Editar
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DeleteIcon />}
                    color="error"
                    onClick={() => {
                      setUserToDelete(user);
                      setOpenDeleteDialog(true);
                    }}
                  >
                    Eliminar
                  </Button>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {users.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron usuarios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || selectedRole || selectedLocation 
              ? 'Intenta con otros filtros de búsqueda'
              : 'Agrega el primer usuario al sistema'
            }
          </Typography>
        </Paper>
      )}

      {/* Dialog para crear/editar usuario */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Nombre requerido' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Nombre completo"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="email"
                  control={control}
                  rules={{ 
                    required: 'Email requerido',
                    pattern: { value: /^\S+@\S+$/i, message: 'Email inválido' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Email"
                      type="email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: !editingUser ? 'Contraseña requerida' : false }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={editingUser ? 'Nueva contraseña (opcional)' : 'Contraseña'}
                      type="password"
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Rol</InputLabel>
                      <Select {...field} label="Rol">
                        <MenuItem value={Role.CASHIER}>Cajero</MenuItem>
                        <MenuItem value={Role.MANAGER}>Gerente</MenuItem>
                        <MenuItem value={Role.ADMIN}>Administrador</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="locationId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Ubicación</InputLabel>
                      <Select {...field} label="Ubicación">
                        <MenuItem value="">Sin asignar</MenuItem>
                        {locations.map((location: any) => (
                          <MenuItem key={location.id} value={location.id}>
                            {location.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="employeeCode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Código de empleado"
                      helperText="Para escaneo con código de barras"
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="commissionRate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Tasa de comisión (%)"
                      type="number"
                      inputProps={{ step: 0.01, min: 0, max: 100 }}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="baseSalary"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Salario base semanal"
                      type="number"
                      inputProps={{ step: 0.01, min: 0 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">L</InputAdornment>,
                      }}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="hireDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Fecha de contratación"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={isCreating || isUpdating}
            >
              {(isCreating || isUpdating) && <CircularProgress size={20} sx={{ mr: 1 }} />}
              {editingUser ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar al usuario <strong>{userToDelete?.name}</strong>?
            Esta acción marcará al usuario como inactivo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting && <CircularProgress size={20} sx={{ mr: 1 }} />}
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;